import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function useStockCountSessions(options: {
  status?: string;
  location_id?: string;
  page?: number;
  page_size?: number;
} = {}) {
  return useQuery({
    queryKey: ['stock-count-sessions', options],
    queryFn: () => api.getStockCountSessions(options),
    staleTime: 5 * 60 * 1000,
  });
}

export function useStockCountSession(sessionId: string) {
  return useQuery({
    queryKey: ['stock-count-session', sessionId],
    queryFn: () => api.getStockCountSession(sessionId),
    enabled: !!sessionId,
  });
}

export function useStockCountItems(sessionId: string, status?: string) {
  return useQuery({
    queryKey: ['stock-count-items', sessionId, status],
    queryFn: () => api.getStockCountItems(sessionId, status),
    enabled: !!sessionId,
  });
}

export function useStockCountSummary(sessionId: string) {
  return useQuery({
    queryKey: ['stock-count-summary', sessionId],
    queryFn: () => api.getStockCountSummary(sessionId),
    enabled: !!sessionId,
  });
}

export function useCreateStockCountSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createStockCountSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-count-sessions'] });
    },
  });
}

export function useUpdateStockCountSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      api.updateStockCountSession(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-count-sessions'] });
    },
  });
}

export function useStartStockCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => api.startStockCount(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-count-sessions'] });
    },
  });
}

export function useUpdateCountItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, itemId, locationId, data }: { 
      sessionId: string;
      itemId: string;
      locationId: string;
      data: any;
    }) => api.updateCountItem(sessionId, itemId, locationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-count-items'] });
      queryClient.invalidateQueries({ queryKey: ['stock-count-summary'] });
    },
  });
}

export function useSubmitStockCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => api.submitStockCount(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-count-sessions'] });
    },
  });
}

export function useApproveStockCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, approvalNotes }: { sessionId: string; approvalNotes?: string }) => 
      api.approveStockCount(sessionId, approvalNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-count-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useRejectStockCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, reason }: { sessionId: string; reason: string }) => 
      api.rejectStockCount(sessionId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-count-sessions'] });
    },
  });
}
